import React from "react";
import FormatHelper from "@client.utils/FormatHelper";
import MeetingDetailsContent from "./ChartWithDetailedTooltip/MeetingDetailsContent";
import DateTypes from "@client.enums/dateTypes";
import GridHeader from "@client.components/GridHeader";
import {
  getIntegerGetter,
  getInvestmentScoreHeader,
  renderIndexValue,
  renderMeetingTypeValue,
} from "@client.components/DataTable/utils";
import Pages from "@client.enums/pages";

const showDetails = (
  payload,
  showModal,
  getDetailedInformation,
  t,
  filter,
  navigate
) => {
  const { tooltip, details } = getDetailedInformation(payload);

  const tooltipTimeTerm =
    filter.dateType === "null"
      ? t("components.MeetingDetailsContent.title.days.customRange", {
          from: filter.dateFrom,
          to: filter.dateTo,
        })
      : FormatHelper.getCountableTermValue(
          t,
          filter.dateType < 0
            ? "components.MeetingDetailsContent.title.days.prior"
            : "components.MeetingDetailsContent.title.days.future",
          filter.dateType == DateTypes.Today ? 1 : filter.dateType,
          {
            days: Math.abs(filter.dateType),
          }
        );
  showModal({
    title: [tooltip, tooltipTimeTerm].join(" "),
    component: (
      <MeetingDetailsContent meetings={payload.meetings} details={details} />
    ),
    contentClass: "meeting-tooltip-details-content",
    footerButtons: [
      {
        icon: "close",
        text: t("components.Modal.FooterButtons.close"),
        onClick: () => Promise.resolve({}),
      },
      {
        icon: "reports",
        text: t("components.Modal.FooterButtons.goToReport"),
        onClick: () =>
          Promise.resolve({}).then(() => {
            navigate(Pages.ReportsMeetings);
            return {};
          }),
      },
    ],
  });
};

const getTotalMeetingCostDetailedInformation = (payload, t) => {
  const { date, dailyMeetings } = payload;

  const meetingQtyPerDay = dailyMeetings.length;

  const tooltip = t("components.TotalMeetingsCost.title", {
    date,
    meetingQtyPerDay,
  });

  const details = t("components.TotalMeetingsCost.details");

  return { details, tooltip };
};

const getTimeSpentDetailedInformation = (payload, t) => {
  const { name } = payload;

  const tooltip = name;

  const details = t(`components.TimeSpent.${name}.details`);

  return { tooltip, details };
};

const getTopMeetingParticipantsDetailedInformation = (payload, t, filter) => {
  let detailsTerm;

  if (payload.meetings.length > 0) {
    if (filter.dateType === 0) {
      detailsTerm = t("components.TopMeetingParticipants.details.today");
    } else {
      detailsTerm =
        filter.dateType > 0
          ? "components.TopMeetingParticipants.details.future"
          : "components.TopMeetingParticipants.details.prior";
    }
  } else {
    detailsTerm = t(
      filter.dateType >= 0
        ? "components.TopMeetingParticipants.details.noMeetings.future"
        : "components.TopMeetingParticipants.details.noMeetings.prior",
      {
        userName: payload.name,
      }
    );
  }

  const tooltip = FormatHelper.getCountableTermValue(
    t,
    "components.TopMeetingParticipants.title",
    payload.meetings.length,
    {
      userName: payload.name,
      meetingQty: payload.meetings.length,
    }
  );

  const details = t(detailsTerm, {
    userName: payload.name,
  });

  return { tooltip, details };
};

const getMeetingCostParticipateGetter = ({ data }, isCostIndexPresentation) => {
  return getIntegerGetter(data, "costValue", isCostIndexPresentation);
};

const getTimeConsumingMeetingColumns = (
  t,
  isCostIndexPresentation,
  isNameExist,
  isMeetingTypeExist,
  showDate
) => {
  const columns = [
    {
      field: "hours",
      tooltipField: "hours",
      headerClass: "data-table-header header-center",
      cellClass: "data-table-cell cell-center",
      maxWidth: 100,
      headerName: t(
        "components.Reports.TimeMeetingParticipantsDataTable.hours"
      ),
      headerComponent: GridHeader,
      cellRenderer: ({ value }) =>
        t(
          "components.Reports.TimeMeetingParticipantsDataTable.hoursCellFormat",
          {
            value,
          }
        ),
    },
    {
      field: "participantsQty",
      tooltipField: "participantsQty",
      headerClass: "data-table-header header-center",
      cellClass: "data-table-cell cell-center",
      maxWidth: 200,
      headerName: t(
        isMeetingTypeExist
          ? "components.TotalMeetingsCost.DataTable.Participate"
          : "components.Reports.TimeMeetingParticipantsDataTable.wmParticipate"
      ),
      headerComponent: GridHeader,
    },
    {
      field: "costValue",
      headerClass: "data-table-header header-center",
      cellClass: "data-table-cell cell-cost-index",
      maxWidth: 250,
      headerName: getInvestmentScoreHeader(
        t,
        "components.Reports.TimeMeetingParticipantsDataTable.mcParticipate",
        isCostIndexPresentation
      ),
      headerComponent: GridHeader,
      valueGetter: (v) =>
        getMeetingCostParticipateGetter(v, isCostIndexPresentation),
      tooltipValueGetter: (v) =>
        getMeetingCostParticipateGetter(v, isCostIndexPresentation),
      valueFormatter: (v) =>
        getMeetingCostParticipateGetter(v, isCostIndexPresentation),
      cellRenderer: ({ value }) =>
        renderIndexValue(value, isCostIndexPresentation),
    },
  ];

  if (!isMeetingTypeExist || showDate) {
    columns.unshift({
      field: "date",
      tooltipField: "date",
      maxWidth: 150,
      headerName: t("components.Reports.TimeMeetingParticipantsDataTable.date"),
      headerComponent: GridHeader,
    });
  }

  if (isNameExist) {
    columns.unshift({
      field: "name",
      tooltipField: "name",
      maxWidth: 150,
      headerName: t("components.Reports.TimeMeetingParticipantsDataTable.name"),
      headerComponent: GridHeader,
    });
  }

  if (isMeetingTypeExist) {
    columns.push({
      field: "meetingType",
      tooltipField: "meetingType",
      minWidth: 150,
      headerClass: "data-table-header header-center",
      cellClass: "data-table-cell cell-center",
      headerName: t(
        "components.Reports.TimeMeetingParticipantsDataTable.meetingType"
      ),
      headerComponent: GridHeader,
      cellRenderer: ({ value }) => renderMeetingTypeValue(t, value),
    });
  }

  return columns;
};

export {
  showDetails,
  getTimeConsumingMeetingColumns,
  getTotalMeetingCostDetailedInformation,
  getTopMeetingParticipantsDetailedInformation,
  getTimeSpentDetailedInformation,
};
